// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides React Query hooks for managing organization-level
 * ledger account defaults. These hooks handle data fetching, caching,
 * and mutations for ledger account configurations.
 */
import {
  getOrganizationLedgerAccounts,
  updateOrganizationLedgerAccounts,
} from '@/account/api/ledger-accounts';
import { LedgerAccounts } from '@/account/api/types';
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';

/**
 * Hook for fetching organization ledger accounts with suspense support.
 * Automatically suspends rendering until data is loaded.
 *
 * @param orgKey - The unique key identifying the organization
 * @returns Query result containing ledger accounts data
 */
export function useOrganizationLedgerAccounts(orgKey: string) {
  return useSuspenseQuery({
    queryKey: ['organization', orgKey, 'accounting'],
    queryFn: () => getOrganizationLedgerAccounts(orgKey),
  });
}

/**
 * Hook for updating organization ledger accounts.
 * Provides mutation function with automatic cache invalidation on success.
 *
 * @returns Mutation object with mutate function and state
 */
export function useUpdateOrganizationLedgerAccounts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orgKey,
      ledgerAccounts,
    }: {
      orgKey: string;
      ledgerAccounts: LedgerAccounts;
    }) => updateOrganizationLedgerAccounts(orgKey, ledgerAccounts),
    onSuccess: async (_, variables) => {
      // Invalidate the specific organization's ledger accounts cache
      await queryClient.invalidateQueries({
        queryKey: ['organization', variables.orgKey, 'accounting'],
      });
    },
  });
}
